import React from 'react';
import { useFilterStore } from '../../store/filterStore';
import { PathologyLab } from '../../types';

interface StatRowProps {
  label: string;
  value: string | number;
  type?: 'positive' | 'neutral' | 'secondary';
}

const StatRow: React.FC<StatRowProps> = ({ label, value, type = 'neutral' }) => {
  const colorClass = {
    positive: 'text-forest-green',
    neutral: 'text-white',
    secondary: 'text-medium-gray',
  }[type];

  return (
    <div className="mb-4">
      <div className="text-sm text-light-gray mb-1">{label}</div>
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
    </div>
  );
};

export const StatsPanel: React.FC = () => {
  const { vertical, pipelineData, activeFilters } = useFilterStore();

  const totalAnalyzed = vertical === 'pathology' ? 5414 : 8200;
  const pipelineCount = pipelineData.length;

  const hasActiveFilters = activeFilters.length > 0;

  // Count platform and addon candidates in filtered results (using labType, not path)
  const platformInResults = pipelineData.filter(
    (item) => 'labType' in item && (item as PathologyLab).labType === 'platform'
  ).length;
  const addonInResults = pipelineData.filter(
    (item) => 'labType' in item && (item as PathologyLab).labType === 'addon'
  ).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-navy-blue text-white px-4 py-3 -mx-6 -mt-6 mb-6">
        <h2 className="font-bold text-lg">PIPELINE METRICS</h2>
      </div>

      {/* Stats */}
      <div className="space-y-4">
        <StatRow
          label={vertical === 'pathology' ? 'Total Labs Analyzed:' : 'Total Practices Analyzed:'}
          value={totalAnalyzed.toLocaleString()}
          type="positive"
        />

        {vertical === 'pathology' && (
          <>
            {/* Investment Strategy Metrics - always show total counts */}
            <div className="mb-4 pb-4 border-b border-light-gray">
              <div className="text-xs text-forest-green font-bold mb-3">INVESTMENT STRATEGY</div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-light-gray">🎯 Platform Candidates:</span>
                  <span className="text-lg font-bold text-white">3</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-light-gray">🔧 Add-on Candidates:</span>
                  <span className="text-lg font-bold text-white">72</span>
                </div>
              </div>
            </div>

            {/* Show filtered results when any filter is active */}
            {hasActiveFilters && pipelineCount > 0 && (
              <div className="mb-4 pb-4 border-b border-light-gray">
                <div className="text-xs text-light-blue font-bold mb-3">FILTERED RESULTS</div>
                <StatRow
                  label="Labs Matching Criteria:"
                  value={pipelineCount}
                  type="positive"
                />
                {/* Show breakdown of strategic candidates within results */}
                {(platformInResults > 0 || addonInResults > 0) && (
                  <div className="space-y-2 mt-2">
                    {platformInResults > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-light-gray">🎯 Platform in results:</span>
                        <span className="text-lg font-bold text-forest-green">{platformInResults}</span>
                      </div>
                    )}
                    {addonInResults > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-light-gray">🔧 Add-on in results:</span>
                        <span className="text-lg font-bold text-light-blue">{addonInResults}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {vertical === 'mso' && (
          <>
            <StatRow
              label="Target Markets:"
              value="9"
              type="positive"
            />

            <StatRow
              label="MSO Target Practices:"
              value="76"
              type="positive"
            />

            {hasActiveFilters && pipelineCount > 0 && (
              <div className="mb-4 pb-4 border-b border-light-gray">
                <div className="text-xs text-light-blue font-bold mb-3">FILTERED RESULTS</div>
                <StatRow
                  label="Practices Matching Criteria:"
                  value={pipelineCount}
                  type="positive"
                />
              </div>
            )}
          </>
        )}

        <StatRow
          label="Selection Rate:"
          value={`${((pipelineCount / totalAnalyzed) * 100).toFixed(2)}%`}
          type="neutral"
        />

        <hr className="border-light-gray my-4" />

        <div>
          <div className="text-sm text-light-gray mb-2">Deal Sources:</div>
          <ul className="text-sm text-light-gray space-y-1">
            <li>• Industry networks</li>
            <li>• Direct outreach</li>
            <li>• Referral partners</li>
            <li>• Proprietary research</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
